import {
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    useSidebarHeaderOffset
} from "./brand";
import {
    type ComposerAttachment,
    MessageScroller,
    NCoreUIKitTheme,
    DropdownMenu,
    Attachment,
    CodeViewer,
    Separator,
    Composer,
    Citation,
    Message,
    Sidebar,
    Avatar,
    Button,
    Bubble,
    Alert,
    Badge,
    Table,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ExternalLink,
    ThumbsDown,
    RefreshCw,
    Sparkles,
    ThumbsUp,
    FileText,
    Share,
    Globe,
    Check,
    Info,
    Copy,
    Plus
} from "lucide-react-native";
import {
    createTransitionIcon,
    createActionIcon,
    createKitIcon
} from "../components/kitIcons";

type HistoryGroup = {
    items: Array<string>;
    id: string;
};

const ExternalLinkIcon = createActionIcon(ExternalLink);

const ThumbsDownIcon = createKitIcon(ThumbsDown);

const RefreshIcon = createActionIcon(RefreshCw);

const SparklesIcon = createKitIcon(Sparkles);

const ThumbsUpIcon = createKitIcon(ThumbsUp);

const ShareIcon = createKitIcon(Share);

const GlobeIcon = createKitIcon(Globe);

const InfoIcon = createKitIcon(Info);

const CopyTransition = createTransitionIcon(Copy, Check);

const PlusIcon = createKitIcon(Plus);

const HISTORY: Array<HistoryGroup> = [
    {
        id: "today",
        items: [
            "h1",
            "h2",
            "h3"
        ]
    },
    {
        id: "yesterday",
        items: [
            "h4",
            "h5",
            "h6"
        ]
    },
    {
        id: "week",
        items: [
            "h7",
            "h8",
            "h9",
            "h10"
        ]
    }
];

const MODELS = [
    "pro",
    "fast",
    "deep"
];

const CHANNELS = [
    {
        amount: "84.000",
        share: "35",
        id: "ch1"
    },
    {
        amount: "60.000",
        share: "25",
        id: "ch2"
    },
    {
        amount: "42.000",
        share: "17.5",
        id: "ch3"
    },
    {
        amount: "36.000",
        share: "15",
        id: "ch4"
    },
    {
        amount: "18.000",
        share: "7.5",
        id: "ch5"
    }
];

const FIRST_SOURCE = {
    url: "https://akademi.nibgat.com",
    domain: "akademi.nibgat.com",
    date: new Date(2026, 9, 12),
    id: "s1"
};

const SOURCES = [
    FIRST_SOURCE,
    {
        url: "https://rapor.nibgat.com",
        date: new Date(2025, 10, 3),
        domain: "rapor.nibgat.com",
        id: "s2"
    }
];

const BUDGET_CODE = [
    "const kisBudcesi = {",
    "  toplam: 240_000,",
    "  para: \"TRY\",",
    "  kanallar: [",
    "    { ad: \"Meta reklamları\", pay: 0.35, tutar: 84_000 },",
    "    { ad: \"Google arama\", pay: 0.25, tutar: 60_000 },",
    "    { ad: \"Influencer\", pay: 0.175, tutar: 42_000 },",
    "    { ad: \"E-posta ve CRM\", pay: 0.15, tutar: 36_000 },",
    "    { ad: \"Yedek\", pay: 0.075, tutar: 18_000 },",
    "  ],",
    "};"
].join("\n");

const getTimestamp = (hours: number, minutes: number) => {
    const timestamp = new Date();

    timestamp.setHours(hours, minutes, 0, 0);

    return timestamp;
};

const FIRST_AT = getTimestamp(10, 12);

const SECOND_AT = getTimestamp(10, 18);

const INITIAL_ATTACHMENTS: Array<ComposerAttachment> = [
    {
        description: "PDF · 1.2 MB",
        title: "kampanya-brifi.pdf",
        icon: FileText,
        id: "brief"
    }
];

const stylesheet = StyleSheet.create({
    page: {
        flexDirection: "column"
    },
    own: {
        alignItems: "flex-end"
    },
    bar: {
        alignItems: "center",
        flexDirection: "row"
    },
    body: {
        flexDirection: "row",
        flex: 1
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    header: {
        alignItems: "center",
        flexDirection: "row"
    },
    title: {
        flexShrink: 1,
        flexGrow: 1
    },
    panel: {
        flexShrink: 0,
        flexGrow: 0
    },
    main: {
        minWidth: 0,
        flex: 1
    },
    scroller: {
        flex: 1
    }
});

const handlePress = () => undefined;

const Chat01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const headerOffset = useSidebarHeaderOffset({
        rowHeight: controlHeights.small
    });

    const {
        height,
        width
    } = useWindowDimensions();

    const [
        activeId,
        setActiveId
    ] = useState("h1");

    const [
        model,
        setModel
    ] = useState("pro");

    const [
        query,
        setQuery
    ] = useState("");

    const [
        isWebSearch,
        setIsWebSearch
    ] = useState(true);

    const [
        attachments,
        setAttachments
    ] = useState<Array<ComposerAttachment>>(INITIAL_ATTACHMENTS);

    const [
        sent,
        setSent
    ] = useState<Array<string>>([]);

    const [
        isCopied,
        setIsCopied
    ] = useState(false);

    useEffect(() => {
        if(!isCopied) {
            return;
        }

        const timer = setTimeout(() => setIsCopied(false), 2000);

        return () => clearTimeout(timer);
    }, [
        isCopied
    ]);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const handleModelChange = (value: string) => setModel(value);

    const handleSearchToggle = () => setIsWebSearch(!isWebSearch);

    const handleSubmit = (text: string) => {
        if(!text.trim()) {
            return;
        }

        setSent((current) => [
            ...current,
            text
        ]);

        setAttachments([]);
    };

    const renderHistoryItem = (id: string) => <Sidebar.MenuItem
        key={id}
    >
        <Sidebar.MenuButton
            title={t(`blocks.chat01.${id}`)}
            onPress={() => setActiveId(id)}
            isActive={activeId === id}
        />
    </Sidebar.MenuItem>;

    const renderHistoryGroup = (group: HistoryGroup) => {
        const items = group.items.filter((id) => t(`blocks.chat01.${id}`).toLowerCase().includes(query.trim().toLowerCase()));

        if(items.length === 0) {
            return null;
        }

        return <Sidebar.Group
            title={t(`blocks.chat01.${group.id}`)}
            key={group.id}
        >
            <Sidebar.Menu>
                {items.map(renderHistoryItem)}
            </Sidebar.Menu>
        </Sidebar.Group>;
    };

    const renderCitation = (index: number) => {
        const source = SOURCES[index - 1] ?? FIRST_SOURCE;

        return <Citation
            sources={[
                {
                    snippet: t(`blocks.chat01.${source.id}Snippet`),
                    title: t(`blocks.chat01.${source.id}Title`),
                    domain: source.domain,
                    date: source.date,
                    url: source.url
                }
            ]}
            index={index}
        />;
    };

    const renderChannel = (channel: typeof CHANNELS[number]) => <Table.Row
        key={channel.id}
    >
        <Table.Cell>
            {t(`blocks.chat01.${channel.id}Name`)}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {t("blocks.chat01.percent", [
                channel.share
            ])}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {t("blocks.chat01.amount", [
                channel.amount
            ])}
        </Table.Cell>
        <Table.Cell>
            {t(`blocks.chat01.${channel.id}Why`)}
        </Table.Cell>
    </Table.Row>;

    const renderSource = (source: typeof SOURCES[number], index: number) => <Card
        variant="outline"
        key={source.id}
    >
        <Card.Header>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Badge
                    title={String(index + 1)}
                    spreadBehaviour="free"
                    variant="tonal"
                    type="neutral"
                    size="small"
                />
                <Card.Description>
                    {source.domain}
                </Card.Description>
            </View>
            <Card.Title>
                {t(`blocks.chat01.${source.id}Title`)}
            </Card.Title>
            <Card.Action>
                <Button
                    accessibilityLabel={t("blocks.chat01.openSource")}
                    icon={ExternalLinkIcon}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </Card.Action>
        </Card.Header>
        <Card.Content
            style={{
                gap: spaces.spacingSm
            }}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t(`blocks.chat01.${source.id}Snippet`)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t(`blocks.chat01.${source.id}Date`)}
            </Text>
        </Card.Content>
    </Card>;

    const renderPanel = () => <>
        <Separator
            orientation="vertical"
        />
        <ScrollView
            style={[
                stylesheet.panel,
                {
                    width: shapeConfigs.cardWidth
                }
            ]}
            contentContainerStyle={{
                padding: spaces.spacingMd,
                gap: spaces.spacingMd
            }}
        >
            <View
                style={[
                    stylesheet.header,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="titleSmallSize"
                    color="high"
                >
                    {t("blocks.chat01.sources")}
                </Text>
                <Badge
                    title={String(SOURCES.length)}
                    spreadBehaviour="free"
                    variant="tonal"
                    type="neutral"
                    size="small"
                />
            </View>
            {SOURCES.map(renderSource)}
            <Alert
                description={t("blocks.chat01.sourcesNote")}
                title={t("blocks.chat01.sourcesNoteTitle")}
                icon={InfoIcon}
                type="neutral"
            />
        </ScrollView>
    </>;

    const handleCopy = () => setIsCopied(true);

    const renderAnswer = () => <Message
        title={t("blocks.chat01.assistant")}
        actionsBehaviour="always"
        avatar={{
            icon: SparklesIcon
        }}
        timestamp={FIRST_AT}
    >
        <Bubble
            isCollapsible={false}
            variant="plain"
        >
            <View
                style={{
                    gap: spaces.spacingMd
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {t("blocks.chat01.a1Part1")}
                    {renderCitation(1)}
                    {t("blocks.chat01.a1Part2")}
                    {renderCitation(2)}
                    {t("blocks.chat01.a1Part3")}
                </Text>
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {t("blocks.chat01.distribution")}
                </Text>
                <Table
                    caption={t("blocks.chat01.distribution")}
                    isCaptionHidden={true}
                    isRaised={true}
                >
                    <Table.Header>
                        <Table.Row>
                            <Table.Head>
                                {t("blocks.chat01.colChannel")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                            >
                                {t("blocks.chat01.colShare")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                            >
                                {t("blocks.chat01.colAmount")}
                            </Table.Head>
                            <Table.Head>
                                {t("blocks.chat01.colReason")}
                            </Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {CHANNELS.map(renderChannel)}
                    </Table.Body>
                </Table>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.chat01.codeIntro")}
                </Text>
                <CodeViewer
                    language="typescript"
                    code={BUDGET_CODE}
                />
            </View>
        </Bubble>
        <Message.Actions>
            <CopyTransition.Provider
                value={isCopied}
            >
                <Button
                    accessibilityLabel={t(isCopied ? "blocks.chat01.copied" : "blocks.chat01.copy")}
                    icon={CopyTransition.Icon}
                    spreadBehaviour="free"
                    onPress={handleCopy}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </CopyTransition.Provider>
            <Button
                accessibilityLabel={t("blocks.chat01.regenerate")}
                spreadBehaviour="free"
                onPress={handlePress}
                icon={RefreshIcon}
                variant="ghost"
                type="neutral"
                size="small"
            />
            <Button
                accessibilityLabel={t("blocks.chat01.like")}
                spreadBehaviour="free"
                onPress={handlePress}
                icon={ThumbsUpIcon}
                variant="ghost"
                type="neutral"
                size="small"
            />
            <Button
                accessibilityLabel={t("blocks.chat01.dislike")}
                spreadBehaviour="free"
                icon={ThumbsDownIcon}
                onPress={handlePress}
                variant="ghost"
                type="neutral"
                size="small"
            />
        </Message.Actions>
    </Message>;

    const renderModelMenu = () => <DropdownMenu
        title={t("blocks.chat01.model")}
    >
        <DropdownMenu.Trigger>
            <Button
                title={isCompact ? undefined : t(`blocks.chat01.model.${model}`)}
                accessibilityLabel={t("blocks.chat01.model")}
                spreadBehaviour="free"
                onPress={handlePress}
                icon={SparklesIcon}
                variant="outline"
                type="neutral"
                size="small"
            />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
            <DropdownMenu.RadioGroup
                title={t("blocks.chat01.model")}
                onChange={handleModelChange}
                value={model}
            >
                {
                    MODELS.map((key) => <DropdownMenu.RadioItem
                        title={t(`blocks.chat01.model.${key}`)}
                        value={key}
                        key={key}
                    />)
                }
            </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
    </DropdownMenu>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            title={t("blocks.chat01.navTitle")}
            collapseBehaviour="offcanvas"
        >
            <Sidebar.Header>
                <Button
                    title={t("blocks.chat01.newChat")}
                    style={{
                        marginTop: headerOffset
                    }}
                    spreadBehaviour="stretch"
                    onPress={handlePress}
                    variant="outline"
                    icon={PlusIcon}
                    type="neutral"
                    size="small"
                />
                <Sidebar.Input
                    aria-label={t("blocks.chat01.searchLabel")}
                    placeholder={t("blocks.chat01.search")}
                    onChangeText={setQuery}
                    shortcut={[
                        "mod",
                        "k"
                    ]}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                {HISTORY.map(renderHistoryGroup)}
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            subTitle={t("blocks.chat01.userEmail")}
                            renderIcon={() => <Avatar
                                title={t("blocks.chat01.userName")}
                                size="xSmall"
                            />}
                            title={t("blocks.chat01.userName")}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.page,
                    {
                        height
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.bar,
                        {
                            paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                            paddingVertical: spaces.spacingMd,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Sidebar.Trigger/>
                    <Text
                        variant="titleMediumSize"
                        style={stylesheet.title}
                        numberOfLines={1}
                        color="high"
                    >
                        {t(`blocks.chat01.${activeId}`)}
                    </Text>
                    {renderModelMenu()}
                    <Button
                        title={isCompact ? undefined : t("blocks.chat01.share")}
                        accessibilityLabel={t("blocks.chat01.share")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="outline"
                        icon={ShareIcon}
                        type="neutral"
                        size="small"
                    />
                </View>
                <Separator />
                <View
                    style={stylesheet.body}
                >
                    <View
                        style={stylesheet.main}
                    >
                        <MessageScroller.Provider
                            defaultScrollPosition="end"
                        >
                            <MessageScroller
                                title={t("blocks.chat01.conversation")}
                                style={stylesheet.scroller}
                            >
                                <MessageScroller.Viewport>
                                    <MessageScroller.Content>
                                        <MessageScroller.Item
                                            messageId="separator"
                                        >
                                            <Separator
                                                title={t("blocks.chat01.today")}
                                                isShowTitle={true}
                                            />
                                        </MessageScroller.Item>
                                        <MessageScroller.Item
                                            messageId="u1"
                                        >
                                            <Message
                                                timestamp={FIRST_AT}
                                                align="end"
                                            >
                                                <Bubble
                                                    variant="user"
                                                >
                                                    {t("blocks.chat01.u1")}
                                                </Bubble>
                                            </Message>
                                        </MessageScroller.Item>
                                        <MessageScroller.Item
                                            messageId="a1"
                                        >
                                            {renderAnswer()}
                                        </MessageScroller.Item>
                                        <MessageScroller.Item
                                            messageId="u2"
                                        >
                                            <View
                                                style={[
                                                    stylesheet.own,
                                                    {
                                                        gap: spaces.spacingSm
                                                    }
                                                ]}
                                            >
                                                <Attachment
                                                    description={t("blocks.chat01.fileInfo")}
                                                    title="kampanya-brifi.pdf"
                                                    onPress={handlePress}
                                                    icon={FileText}
                                                />
                                                <Message
                                                    timestamp={SECOND_AT}
                                                    align="end"
                                                >
                                                    <Bubble
                                                        variant="user"
                                                    >
                                                        {t("blocks.chat01.u2")}
                                                    </Bubble>
                                                </Message>
                                            </View>
                                        </MessageScroller.Item>
                                        {
                                            sent.map((text, index) => <MessageScroller.Item
                                                messageId={`sent-${index}`}
                                                key={`sent-${index}`}
                                            >
                                                <Message
                                                    deliveryStatus="sent"
                                                    align="end"
                                                >
                                                    <Bubble
                                                        variant="user"
                                                    >
                                                        {text}
                                                    </Bubble>
                                                </Message>
                                            </MessageScroller.Item>)
                                        }
                                        <MessageScroller.Item
                                            messageId="thinking"
                                        >
                                            <Message
                                                avatar={{
                                                    icon: SparklesIcon
                                                }}
                                            >
                                                <Bubble
                                                    isCollapsible={false}
                                                    variant="plain"
                                                >
                                                    <View
                                                        style={[
                                                            stylesheet.row,
                                                            {
                                                                gap: spaces.spacingSm
                                                            }
                                                        ]}
                                                    >
                                                        <Bubble.Typing />
                                                        <Text
                                                            variant="bodyMediumSize"
                                                            color="mid"
                                                        >
                                                            {t("blocks.chat01.thinking")}
                                                        </Text>
                                                    </View>
                                                </Bubble>
                                            </Message>
                                        </MessageScroller.Item>
                                    </MessageScroller.Content>
                                </MessageScroller.Viewport>
                                <MessageScroller.Button />
                                <MessageScroller.Footer>
                                    <Composer
                                        placeholder={t("blocks.chat01.composerPlaceholder")}
                                        onAttachmentsChange={setAttachments}
                                        attachments={attachments}
                                        onSubmit={handleSubmit}
                                    >
                                        <Composer.Actions>
                                            <Composer.Action
                                                title={t("blocks.chat01.webSearch")}
                                                onPress={handleSearchToggle}
                                                isPressed={isWebSearch}
                                                icon={GlobeIcon}
                                            />
                                        </Composer.Actions>
                                    </Composer>
                                    <Text
                                        style={{
                                            marginTop: spaces.spacingSm
                                        }}
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.chat01.disclaimer")}
                                    </Text>
                                </MessageScroller.Footer>
                            </MessageScroller>
                        </MessageScroller.Provider>
                    </View>
                    {isRoomy ? renderPanel() : null}
                </View>
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Chat01;
